/**
 * Validates a file against allowed extensions, magic bytes, and malicious tags.
 * @param {File} file - The file to validate.
 * @param {string} type - The asset type: 'image' or 'icon' (default 'image').
 * @returns {Promise<{isValid: boolean, error: string|null}>}
 */
const MAX_FILE_SIZE_MB = 10;
const MAX_REQUEST_SIZE_MB = 15;

export const validateFile = async (file, type = 'image') => {
  const IMAGE_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif'];
  const ICON_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'svg'];

  const isIcon = type === 'icon';
  const allowedExtensions = isIcon
    ? ICON_ALLOWED_EXTENSIONS
    : IMAGE_ALLOWED_EXTENSIONS;

  // 1. Extension Check
  const nameParts = file.name.split('.');
  const extension = nameParts[nameParts.length - 1].toLowerCase();
  if (!allowedExtensions.includes(extension)) {
    return {
      isValid: false,
      error: `Invalid file extension: .${extension}. Allowed: ${allowedExtensions.join(
        ', ',
      )}`,
    };
  }

  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return {
      isValid: false,
      error: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`,
    };
  }

  // 3. MIME type check
  if (file.type) {
    const allowedMimeTypes = isIcon
      ? ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml']
      : ['image/jpeg', 'image/png', 'image/gif'];

    if (!allowedMimeTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'MIME type mismatch or invalid',
      };
    }
  }

  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onloadend = (e) => {
      const buffer = e.target.result;
      const arr = new Uint8Array(buffer);

      if (extension === 'svg') {
        const decoder = new TextDecoder();
        const textContent = decoder.decode(arr).toLowerCase();

        // SVG-specific malicious content check
        const maliciousSvgPatterns = [
          '<?php',
          '<?=',
          '<script',
          '<script language="php">',
          'auto_prepend_file',
          'run-at="server"',
        ];

        for (const pattern of maliciousSvgPatterns) {
          if (textContent.includes(pattern)) {
            console.warn(
              `[SECURITY] Blocked upload attempt: Malicious SVG pattern "${pattern}" detected in file ${file.name}`,
            );
            return resolve({
              isValid: false,
              error: 'Invalid file: potential malicious SVG content detected',
            });
          }
        }

        // Validate SVG has a valid XML processing instruction or root element
        const prefix = textContent.trim().substring(0, 200);
        if (!prefix.startsWith('<?xml') && !prefix.startsWith('<svg')) {
          return resolve({
            isValid: false,
            error:
              'Invalid SVG file: does not start with expected XML/SVG root',
          });
        }

        return resolve({ isValid: true, error: null });
      }

      // --- 4. Magic Byte Check (images only) ---
      let header = '';
      for (let i = 0; i < Math.min(arr.length, 4); i++) {
        header += arr[i].toString(16).padStart(2, '0').toUpperCase();
      }

      let isValidType = false;
      // JPG: FFD8FF
      // PNG: 89504E47
      // GIF: 47494638
      if (header.startsWith('FFD8FF')) {
        isValidType = true;
      } else if (header.startsWith('89504E47')) {
        isValidType = true;
      } else if (header.startsWith('47494638')) {
        isValidType = true;
      }

      if (!isValidType) {
        return resolve({
          isValid: false,
          error:
            'File content does not match its extension (Magic Byte mismatch).',
        });
      }

      // --- 5. Deep Scan (Polyglot/RCE Prevention) ---
      // Scan for suspicious strings (PHP tags, scripts, etc.)
      const decoder = new TextDecoder();
      const textContent = decoder.decode(arr).toLowerCase();

      const maliciousPatterns = [
        '<?php',
        '<?=',
        '<script',
        'eval(',
        'base64_decode',
        'system(',
        'shell_exec',
        'passthru',
        'popen(',
        'proc_open',
      ];

      for (const pattern of maliciousPatterns) {
        if (textContent.includes(pattern)) {
          console.warn(
            `[SECURITY] Blocked upload attempt: Malicious pattern "${pattern}" detected in file ${file.name}`,
          );
          return resolve({
            isValid: false,
            error: 'File contains forbidden content or malicious tags.',
          });
        }
      }

      resolve({ isValid: true, error: null });
    };

    reader.onerror = () => {
      resolve({ isValid: false, error: 'Error reading file.' });
    };

    // SVG is text-based; read enough to validate the root element and scan patterns.
    // For images, reading the first 4KB is enough for magic bytes and polyglot scan.
    reader.readAsArrayBuffer(extension === 'svg' ? file : file.slice(0, 4096));
  });
};

/**
 * Validates that the combined size of the selected files does not exceed the
 * per-request upload limit (15MB).
 * @param {File[]} files - Files to validate.
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateTotalFileSize = (files) => {
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);

  if (totalSize > MAX_REQUEST_SIZE_MB * 1024 * 1024) {
    return {
      isValid: false,
      error: `Total file size exceeds ${MAX_REQUEST_SIZE_MB}MB limit.`,
    };
  }

  return { isValid: true, error: null };
};
